import { useState } from 'react';
import { updateUserEmail } from '@/services/settings';

interface EmailChangeConfirmationProps {
  currentEmail: string;
  newEmail: string;
  userId: string;
  onCompleted: () => void;
  onCancel: () => void;
  onBack: () => void;
}

export function EmailChangeConfirmation({ 
  currentEmail, 
  newEmail, 
  userId,
  onCompleted, 
  onCancel: _onCancel,
  onBack 
}: EmailChangeConfirmationProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleConfirmChange = async () => {
    setIsLoading(true);
    setError('');

    try {
      const result = await updateUserEmail(userId, newEmail);
      
      if (result.success) {
        onCompleted();
      } else {
        setError(result.error || '이메일 변경에 실패했습니다.');
      }
    } catch (error) {
      console.error('Email change error:', error);
      setError('이메일 변경 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div className="text-center mb-6">
        <h4 className="text-base font-semibold text-gray-900 mb-2">
          이메일 변경 확인
        </h4>
        <p className="text-sm text-gray-600">
          아래 정보가 맞는지 확인하고 변경을 완료해주세요
        </p>
      </div>

      {/* 이메일 변경 정보 */}
      <div className="space-y-4 mb-6">
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">현재 이메일</span>
            <span className="text-sm text-gray-500">변경 전</span>
          </div>
          <div className="text-gray-900 font-medium">
            {currentEmail}
          </div>
        </div>

        <div className="flex items-center justify-center">
          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>

        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-green-700">새 이메일</span>
            <span className="text-sm text-green-600">변경 후</span>
          </div>
          <div className="text-green-900 font-medium">
            {newEmail}
          </div>
        </div>
      </div>

      {/* 주의사항 */}
      <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200 mb-6">
        <div className="flex items-start gap-3">
          <svg className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <div>
            <h5 className="text-sm font-medium text-yellow-800 mb-1">주의사항</h5>
            <ul className="text-sm text-yellow-700 space-y-1">
              <li>• 이메일 변경 후 새로운 이메일로 로그인해야 합니다</li>
              <li>• 변경된 이메일 주소로 향후 모든 알림이 발송됩니다</li>
              <li>• 이 작업은 되돌릴 수 없습니다</li>
            </ul>
          </div>
        </div>
      </div>

      {/* 에러 메시지 */}
      {error && (
        <div className="text-red-500 text-sm bg-red-50 p-3 rounded-lg border border-red-200 mb-4">
          {error}
        </div>
      )}

      {/* 버튼들 */}
      <div className="flex gap-3">
        <button
          onClick={onBack}
          disabled={isLoading}
          className="flex-1 py-3 px-4 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          이전
        </button>
        
        <button
          onClick={handleConfirmChange}
          disabled={isLoading}
          className={`flex-1 py-3 px-4 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
            !isLoading
              ? 'bg-[#4CD964] text-white hover:bg-[#45B85C]'
              : 'bg-gray-300 text-gray-500'
          }`}
        >
          {isLoading ? (
            <div className="flex items-center justify-center gap-2">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              변경 중...
            </div>
          ) : (
            '이메일 변경하기'
          )}
        </button>
      </div>
    </div>
  );
}